/**
 * Every plan gate and permission the bulk flow needs, in one place.
 *
 * A bulk send produces one ordinary envelope per recipient, so it is gated
 * exactly as a single send is — same feature keys, same permissions, same
 * "disabled with a lock and a reason" treatment. The tooltip wording follows
 * `SignerInputFields`: an admin-disabled feature says so (using the admin's
 * own message where there is one), a feature outside the plan gets the upsell
 * line.
 */

import { useMemo } from "react";
import FeatureKeys from "global/constants/FeatureKeys";
import strings from "global/constants/StringConstants";
import { FeatureState, useFeatureState } from "hooks/use-feature";
import {
  hasAccessTo,
  hasWorkspacePermission,
  isExternalUser,
} from "layouts/Config";

export interface BulkGate {
  available: boolean;
  /** Empty when the feature is available — the caller shows no tooltip then. */
  tooltip: string;
}

const toGate = (state: FeatureState): BulkGate => {
  if (state.status === strings.FEATURE_STATUS_AVAILABLE) {
    return { available: true, tooltip: "" };
  }
  return {
    available: false,
    tooltip:
      state.status === strings.FEATURE_STATUS_ADMIN_DISABLED
        ? ((state as any).disabledMessage ??
          strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP)
        : strings.FEATURE_GATE_UPSELL_TOOLTIP,
  };
};

export interface BulkGates {
  /** The flow itself. Reaching `/bulk/new` by URL is still a way in. */
  bulkSend: BulkGate;
  sendEmail: BulkGate;
  sendSms: BulkGate;
  authOtp: BulkGate;
  authPasscode: BulkGate;
  authPhone: BulkGate;
  reminders: BulkGate;
  autoExpiration: BulkGate;
  scheduledSend: BulkGate;
  delegation: BulkGate;
  delayedRouting: BulkGate;
  /** Permission to start a send at all, as the create menu checks it. */
  canCreate: boolean;
  /** Permission to send, checked before the batch actually goes out. */
  canSend: boolean;
}

export const useBulkGates = (): BulkGates => {
  const bulkSend = useFeatureState(FeatureKeys.ENVELOPE_BULK_SEND);
  const sendEmail = useFeatureState(FeatureKeys.ENVELOPE_SEND_EMAIL);
  const sendSms = useFeatureState(FeatureKeys.ENVELOPE_SEND_SMS);
  const authOtp = useFeatureState(FeatureKeys.AUTH_OTP);
  const authPasscode = useFeatureState(FeatureKeys.AUTH_PASSCODE);
  const authPhone = useFeatureState(FeatureKeys.AUTH_PHONE);
  const reminders = useFeatureState(FeatureKeys.REMINDER_AUTOMATIC);
  const autoExpiration = useFeatureState(FeatureKeys.ENVELOPE_AUTO_EXPIRATION);
  const scheduledSend = useFeatureState(FeatureKeys.ENVELOPE_SCHEDULED_SEND);
  const delegation = useFeatureState(FeatureKeys.ENVELOPE_DELEGATION);
  const delayedRouting = useFeatureState(FeatureKeys.ENVELOPE_DELAYED_ROUTING);

  return useMemo(
    () => ({
      bulkSend: toGate(bulkSend),
      sendEmail: toGate(sendEmail),
      sendSms: toGate(sendSms),
      authOtp: toGate(authOtp),
      authPasscode: toGate(authPasscode),
      authPhone: toGate(authPhone),
      reminders: toGate(reminders),
      autoExpiration: toGate(autoExpiration),
      scheduledSend: toGate(scheduledSend),
      delegation: toGate(delegation),
      delayedRouting: toGate(delayedRouting),
      // An external user is a signer, never a sender — same rule the create
      // menu applies.
      canCreate:
        !isExternalUser() &&
        hasAccessTo(strings.resources.sign, strings.addPermission) &&
        hasWorkspacePermission("document.create"),
      canSend:
        !isExternalUser() &&
        hasAccessTo(strings.resources.sign, strings.sendPermission) &&
        hasWorkspacePermission("document.send"),
    }),
    [
      bulkSend,
      sendEmail,
      sendSms,
      authOtp,
      authPasscode,
      authPhone,
      reminders,
      autoExpiration,
      scheduledSend,
      delegation,
      delayedRouting,
    ],
  );
};

export default useBulkGates;
